import { FormEvent, useEffect, useState } from 'react';
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { Button, Card, Input } from '@bruno/ui';
import { useAuth } from '../auth/AuthContext';
import { resetPassword } from '../api/account';
import { ApiError } from '../api/client';

export function ResetPasswordPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [twoFactorOpen, setTwoFactorOpen] = useState(false);
  const [twoFactorMode, setTwoFactorMode] = useState<'totp' | 'recovery'>('totp');
  const [twoFactorCode, setTwoFactorCode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (done) {
      const t = setTimeout(() => navigate('/login'), 2500);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [done, navigate]);

  if (user) return <Navigate to="/" replace />;

  if (!token) {
    return (
      <div className="min-h-screen grid place-items-center bg-slate-900 px-6">
        <Card className="w-full max-w-md bg-white">
          <h1 className="text-xl font-semibold">Reset password</h1>
          <p className="text-sm text-red-600 mt-2">
            This link is missing a reset token. Request a new email from{' '}
            <Link to="/forgot-password" className="text-indigo-600 hover:underline">
              Forgot password
            </Link>
            .
          </p>
        </Card>
      </div>
    );
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    if (password !== confirm) {
      setError('Passwords do not match.');
      return;
    }
    setSubmitting(true);
    try {
      await resetPassword({
        token,
        newPassword: password,
        twoFactorCode:
          twoFactorOpen && twoFactorMode === 'totp' && twoFactorCode.trim()
            ? twoFactorCode.trim()
            : undefined,
        recoveryCode:
          twoFactorOpen && twoFactorMode === 'recovery' && twoFactorCode.trim()
            ? twoFactorCode.trim()
            : undefined,
      });
      setDone(true);
    } catch (err) {
      if (err instanceof ApiError && err.code === 'bad_request') {
        const msg = err.message || '';
        if (/two_factor_required/i.test(msg)) {
          setTwoFactorOpen(true);
          setError('Two-factor authentication is enabled on this account. Enter a code below.');
        } else if (/invalid_token/i.test(msg)) {
          setError('This reset link is invalid or has expired. Request a new one.');
        } else {
          setError(err.message);
        }
      } else if (err instanceof ApiError && err.code === 'unauthorized') {
        setError('Invalid two-factor code.');
      } else {
        setError(err instanceof Error ? err.message : 'Reset failed');
      }
    } finally {
      setSubmitting(false);
    }
  }

  if (done) {
    return (
      <div className="min-h-screen grid place-items-center bg-slate-900 px-6">
        <Card className="w-full max-w-md bg-white">
          <h1 className="text-xl font-semibold">Password updated</h1>
          <p className="text-sm text-slate-600 mt-2">
            All other devices have been signed out for security. Redirecting you to sign in…
          </p>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen grid place-items-center bg-slate-900 px-6">
      <Card className="w-full max-w-md bg-white">
        <h1 className="text-xl font-semibold">Choose a new password</h1>
        <form onSubmit={onSubmit} className="flex flex-col gap-4 mt-5">
          <Input
            label="New password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            minLength={8}
            required
            autoFocus
            autoComplete="new-password"
          />
          <Input
            label="Confirm new password"
            type="password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            minLength={8}
            required
            autoComplete="new-password"
          />
          <button
            type="button"
            className="text-left text-sm text-slate-600 hover:text-slate-900"
            onClick={() => setTwoFactorOpen((o) => !o)}
          >
            {twoFactorOpen ? '▾' : '▸'} I have two-factor authentication enabled
          </button>
          {twoFactorOpen ? (
            <div className="flex flex-col gap-3 pl-3 border-l-2 border-slate-200">
              <Input
                label={twoFactorMode === 'totp' ? 'Authenticator code' : 'Recovery code'}
                value={twoFactorCode}
                onChange={(e) => setTwoFactorCode(e.target.value)}
                inputMode={twoFactorMode === 'totp' ? 'numeric' : 'text'}
                autoComplete="one-time-code"
              />
              <button
                type="button"
                className="text-xs text-indigo-600 hover:underline self-start"
                onClick={() => {
                  setTwoFactorMode((m) => (m === 'totp' ? 'recovery' : 'totp'));
                  setTwoFactorCode('');
                }}
              >
                {twoFactorMode === 'totp'
                  ? 'Use a recovery code instead'
                  : 'Use my authenticator app instead'}
              </button>
            </div>
          ) : null}
          {error ? <div className="text-sm text-red-600">{error}</div> : null}
          <Button type="submit" loading={submitting}>
            Reset password
          </Button>
        </form>
      </Card>
    </div>
  );
}
